import type { BurstChamber, FlipPad, Hazard, PulseChart } from '../../chart.js';
import type { Surface } from '../../gameTypes.js';
import { MAX_HP, STARTING_HP } from '../../gameTypes.js';

/**
 * Verdicts Pulse actually produces. The shared {@link
 * import('../../gameTypes.js').JudgeVerdict} also includes 'ok', but
 * Pulse never opts into the OK mercy band (it doesn't pass
 * `okWindowMs` to {@link import('../../scoring.js').judge}). Keeping
 * this narrow keeps the downstream HP / rendering / event handlers
 * exhaustive at compile time — a future change adding the OK band
 * to Pulse would force the consumers to handle it instead of
 * silently producing a resolution the visuals can't render.
 */
export type PulseVerdict = 'perfect' | 'good' | 'miss';

export interface PadResolution {
  padId: string;
  verdict: PulseVerdict;
  diffMs: number;
  beat: number;
  registeredAt: number;
}

/**
 * Reason a run ended in failure. Currently only one — runs end when HP
 * hits zero, whether from missed pads, hazards, or empty taps. Kept as
 * a string union so the friendly-message renderer can grow new reasons
 * without having to widen the type.
 */
export type DeathReason = 'drained';

export interface EngineState {
  beat: number;
  currentSurface: Surface;
  combo: number;
  maxCombo: number;
  perfects: number;
  goods: number;
  misses: number;
  score: number;
  resolutions: PadResolution[];
  alive: boolean;
  completed: boolean;
  deathReason: DeathReason | null;
  deathBeat: number | null;
  hitPadIds: Set<string>;
  flipHeld: boolean;
  /**
   * Health-point gauge (PIU/StepMania life-bar style). Starts at
   * STARTING_HP, capped at MAX_HP, alive=false when it hits 0.
   */
  hp: number;
  maxHp: number;
  /**
   * Beat at which the most recent hazard hit happened. Used to grant a
   * brief invulnerability window so a player can't be chunked repeatedly
   * while crossing a long spike strip.
   */
  lastHazardHitBeat: number | null;
  /**
   * Number of consecutive misses in the current streak. Looked up against
   * HP_MISS_DAMAGE_BY_STREAK_INDEX to compute decaying damage so a single
   * bad bar doesn't snowball into death. Reset on any non-miss
   * (perfect/good) required-pad resolution.
   */
  consecutiveMissCount: number;
  /**
   * Beat of the most recent miss (input miss or auto-miss expiry). Used
   * to gate passive HP regen behind a quiet window so the player has to
   * actually demonstrate clean play before HP claws back.
   */
  lastMissBeat: number | null;
  /**
   * Accumulator of "eligible" beats since the last regen tick. We add
   * to this in advanceTo() across the eligible portion of each step and
   * spawn a +1 HP regen event each time it crosses
   * HP_REGEN_INTERVAL_BEATS. Eligibility = combo > 0 and at least
   * HP_REGEN_QUIET_BEATS since the last miss.
   */
  regenProgressBeats: number;
  /**
   * Number of taps the player has banked inside each burst chamber
   * (keyed by chamber id). Updated as taps land while the chamber is
   * active; the bonus is awarded when the chamber closes.
   */
  burstTaps: Map<string, number>;
  /**
   * Burst chambers whose end beat has been crossed and whose completion
   * bonus has been awarded. Stays in the set for the rest of the run so
   * we never double-fire the completion event.
   */
  completedBurstChambers: Set<string>;
  /**
   * Beat of the most recent scoring burst tap, tracked globally across
   * the whole run (chambers can sit back-to-back — the validator only
   * forbids overlap, not adjacency — so a per-chamber counter would let
   * an autoclicker reset the cap at every boundary). Used to enforce
   * BURST_CHAMBER_MAX_CPS: taps arriving within `config.burstTapMinBeatGap`
   * of this beat are dropped before they score. Human mashing tops out
   * well below the cap, so the carry-over isn't observable in normal play.
   */
  lastBurstScoringBeat: number | null;
}

export interface EngineConfig {
  chart: PulseChart;
  perfectWindowBeats: number;
  goodWindowBeats: number;
  flipTransitionBeats: number;
  /**
   * Minimum spacing in beats between any two scoring burst taps,
   * enforced globally across the run (not per-chamber). Derived from
   * BURST_CHAMBER_MAX_CPS and chart BPM at config-build time so the
   * cap is BPM-correct per map.
   */
  burstTapMinBeatGap: number;
}

export function createInitialState(chart: PulseChart): EngineState {
  return {
    beat: 0,
    currentSurface: chart.spawnSurface,
    combo: 0,
    maxCombo: 0,
    perfects: 0,
    goods: 0,
    misses: 0,
    score: 0,
    resolutions: [],
    alive: true,
    completed: false,
    deathReason: null,
    deathBeat: null,
    hitPadIds: new Set(),
    flipHeld: false,
    hp: STARTING_HP,
    maxHp: MAX_HP,
    lastHazardHitBeat: null,
    consecutiveMissCount: 0,
    lastMissBeat: null,
    regenProgressBeats: 0,
    burstTaps: new Map(),
    completedBurstChambers: new Set(),
    lastBurstScoringBeat: null,
  };
}

export function buildHazardLookup(hazards: Hazard[]): Hazard[] {
  return [...hazards].sort((a, b) => a.startBeat - b.startBeat);
}

export function buildPadLookup(pads: FlipPad[]): FlipPad[] {
  return [...pads].sort((a, b) => a.beat - b.beat);
}

export function buildBurstChamberLookup(
  bursts: ReadonlyArray<BurstChamber> | undefined,
): BurstChamber[] {
  if (!bursts) {
    return [];
  }
  return [...bursts].sort((a, b) => a.startBeat - b.startBeat);
}

/**
 * Reset the chart-relative parts of an engine state so the next loop
 * sees a fresh chart but keeps the player's accumulated score/combo
 * (and verdict counters, so the results overlay shows total perfects
 * across all loops). HP resets to max so a near-death loop-1 finish
 * doesn't snap-kill the player on the first hazard of loop 2.
 *
 * Used by both the live client session and the server-side headless
 * runner — they MUST agree on what carries over between loops, or
 * replay validation drifts out of sync the moment a multi-loop run
 * is re-simulated.
 *
 * Mutates the passed state in place.
 */
export function resetEngineStateForNextLoop(state: EngineState, chart: PulseChart): void {
  state.beat = 0;
  state.currentSurface = chart.spawnSurface;
  state.resolutions = [];
  state.alive = true;
  state.completed = false;
  state.deathReason = null;
  state.deathBeat = null;
  state.hitPadIds = new Set();
  state.flipHeld = false;
  state.hp = state.maxHp;
  state.lastHazardHitBeat = null;
  state.consecutiveMissCount = 0;
  state.lastMissBeat = null;
  state.regenProgressBeats = 0;
  state.burstTaps = new Map();
  state.completedBurstChambers = new Set();
  state.lastBurstScoringBeat = null;
  // Note: score, combo, maxCombo, perfects, goods, misses are
  // intentionally preserved — they accumulate across loops so the
  // results overlay can show the run total.
}
